import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  ClipboardList,
  ShieldCheck,
  Hash,
  Settings,
  ShieldAlert,
  LogOut,
  X,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const navItems = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Company Details', path: '/companies', icon: Building2 },
  { name: 'Request Forms', path: '/request-forms', icon: ClipboardList },
  { name: 'Employee Limits', path: '/employee-limits', icon: Users },
  { name: 'Employee ID Settings', path: '/employee-id-settings', icon: Hash },
  { name: 'Admin Access', path: '/admin-access', icon: ShieldCheck },
  { name: 'Settings', path: '/settings', icon: Settings },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-md md:hidden transition-opacity"
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-white/95 backdrop-blur-xl border-r border-slate-200/80 flex flex-col justify-between transition-transform duration-300 ease-in-out md:translate-x-0 shadow-[4px_0_24px_rgba(0,0,0,0.02)] ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand Header */}
        <div>
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center space-x-3">
              {/* Bigger Brand Logo Container */}
              <div className="p-2 rounded-2xl bg-white border border-slate-200/90 shadow-md shadow-slate-200/50 ring-4 ring-teal-500/10 flex items-center justify-center flex-shrink-0">
                <img
                  src="/savrion_icon.png"
                  alt="Savrion Technologies Logo"
                  className="w-10 h-10 object-contain hover:scale-105 transition-transform duration-200"
                />
              </div>
              {/* Perfectly Aligned Text */}
              <div className="flex flex-col justify-center min-w-0">
                <div className="flex items-center gap-1.5 leading-none">
                  <h1 className="font-display font-black text-lg text-slate-900 tracking-tight">
                    SuperAdmin
                  </h1>
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 uppercase tracking-widest leading-none">
                    SAVRION
                  </span>
                </div>
                <p className="text-[10px] uppercase font-extrabold text-slate-400 tracking-wider mt-1 leading-none">
                  Platform Core
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-900 hover:bg-slate-100/80 md:hidden transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="px-3.5 py-6 space-y-1.5">
            <div className="px-3 pb-2 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
              Navigation Menu
            </div>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `group relative flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-emerald-50 to-teal-50/60 text-emerald-700 font-bold shadow-sm border border-emerald-200/50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50/80'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div className="flex items-center space-x-3">
                        <div className={`p-1.5 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30' 
                            : 'text-slate-400 group-hover:text-emerald-600 group-hover:bg-emerald-50'
                        }`}>
                          <Icon className="w-4 h-4 transition-transform group-hover:scale-110" />
                        </div>
                        <span className="tracking-tight">{item.name}</span>
                      </div>
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${
                        isActive ? 'text-emerald-600 opacity-100 translate-x-0' : 'text-slate-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0'
                      }`} />
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Footer Profile & Logout */}
        <div className="p-3.5 m-3 rounded-2xl bg-slate-50/80 border border-slate-200/60 flex items-center justify-between shadow-sm">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="relative flex-shrink-0">
              <img
                src={user?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80'}
                alt={user?.name}
                className="w-9 h-9 rounded-xl object-cover ring-2 ring-emerald-500/20 shadow-sm"
              />
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
            </div>
            <div className="truncate">
              <p className="text-xs font-extrabold text-slate-900 truncate font-display">{user?.name}</p>
              <p className="text-[10px] text-slate-500 font-medium truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            title="Logout"
            className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100 transition duration-150 flex-shrink-0"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

